import { AddInscriptionModalTitles } from 'routes/BudgetModule/components/shared/InscriptionsGrid/AddInscription/types';
import _ from 'lodash';
import { BudgetExpenseOrIncome } from 'business/constants';
import { FilterOperations as FO } from 'business/definitions';
import {
    EXPENSE_INCOME_FIELD,
    INSCRIPTION_EXPENSE_INCOME,
    ORG_CHART_FINANCE_FIELD,
    PROJECT_ID,
} from 'routes/BudgetModule/config/constants';
import { getFiltersFromTCDNode } from 'ui/AgGridReact/helpers';
import { ColDef, GridApi, IRowNode } from 'ag-grid-community';
import {
    getInscriptionsGridColumns,
    getInscriptionsGridColumnsWithPhase,
} from '../../../InscriptionsGrid/gridHelpers';
import { TFunction } from 'i18next';
import {
    BudgetProposalT,
    ResourceNameField,
} from 'routes/BudgetModule/config/types';
import { getIsEditablePhase } from 'routes/BudgetModule/shared/phases/helpers';
import { Project } from 'routes/BudgetModule/shared/useGetProjects';
import { getImputationAsString } from 'routes/BudgetModule/config/hooks';
import { translateBlockValue } from 'i18n';
import { replaceBudgetLineFilterToInscriptionFilter } from 'routes/BudgetModule/helpers/query';
import { getClientBind } from 'store/APIClient';
import { isAmp } from 'routes/BudgetModule/helpers/bind';

export const getAddInscriptionStepTitles = (
    t: TranslationT,
    resourceNameField: ResourceNameField,
): AddInscriptionModalTitles => {
    const resourceNameLower = translateBlockValue(
        resourceNameField,
        true,
    ).toLowerCase();
    const bind = getClientBind();
    return {
        budgetProposalSelection: {
            title: t('addInscriptionModal.budgetProposalSelectionTitle', {
                dimension: `${resourceNameLower}`,
            }),
            subTitle: t('inscriptionsModal.budgetProposalSelectionSubtitle', {
                dimension: `${resourceNameLower}`,
                gender: isAmp(bind) ? 'auxquelles' : 'auxquels',
            }),
        },
        budgetLineSelection: {
            title: t('addInscriptionModal.budgetLineSelectionTitle'),
            subTitle: t('inscriptionsModal.budgetLineSelectionSubtitle', {
                dimension: `${resourceNameLower}`,
            }),
        },
    };
};

const mergeFiltersWithPriority = (
    currentFilters: QueryFilterT[],
    additionalFilters: QueryFilterT[],
) => {
    additionalFilters.forEach((newFilter) => {
        let found = false;
        currentFilters.forEach((currentFilter) => {
            if (currentFilter.field === newFilter.field) {
                found = true;
                currentFilter.operation = newFilter.operation;
                currentFilter.value = newFilter.value;
            }
        });
        if (!found) {
            currentFilters.push(newFilter);
        }
    });
};

const parseOrgChartTreeValue = (value: string): string => {
    try {
        return JSON.parse(value).value;
    } catch {
        return value;
    }
};

export const getInscriptionsQueryFilters = (
    baseFilters: QueryFilterT[],
    colDef: ColDef,
    node: IRowNode,
    gridApi: GridApi,
): QueryFilterT[] => {
    const fixedBaseFilters = baseFilters
        .map((filter) => _.pick(filter, ['field', 'operation', 'value']))
        .filter(
            (f) =>
                f.operation === FO.is_null ||
                f.operation === FO.is_not_null ||
                f.value,
        );

    const additionalFilters = getFiltersFromTCDNode(
        colDef.pivotKeys || [],
        node,
        gridApi,
    )
        .map((filter) => {
            if (filter.field === ORG_CHART_FINANCE_FIELD) {
                const updatedFilter: QueryFilterT = {
                    ...filter,
                    operation: FO.equal,
                    value: filter?.value
                        ? parseOrgChartTreeValue(filter.value)
                        : null,
                };
                return updatedFilter;
            }
            return filter;
        })
        .filter((filter) => filter.value !== 'unknown');

    mergeFiltersWithPriority(fixedBaseFilters, additionalFilters);
    return fixedBaseFilters.map(replaceBudgetLineFilterToInscriptionFilter);
};

export const getExpenseOrIncomeFromFilters = (
    filters: QueryFilterT[],
): BudgetExpenseOrIncome => {
    const candidateExpenseOrIncome = filters.find(
        (filter) =>
            filter.field === INSCRIPTION_EXPENSE_INCOME ||
            filter.field === EXPENSE_INCOME_FIELD,
    )?.value;

    if (candidateExpenseOrIncome === undefined) {
        console.error('expenseOrIncome is null for filters', filters);
    }

    return (
        BudgetExpenseOrIncome[
            candidateExpenseOrIncome as keyof typeof BudgetExpenseOrIncome
        ] ?? BudgetExpenseOrIncome.EXPENSE
    );
};

const getInscriptionsModalOrderedColumns = (columns: ColDef[]): ColDef[] => {
    const order = [
        'details',
        'status',
        'originalInscription',
        'amount',
        'comment',
        'imputation',
        PROJECT_ID,
        'actions',
    ];
    return _.sortBy(columns, (obj) => _.indexOf(order, obj.field));
};

// TODO: Modify as part of epic "Améliorer la gestion des col defs de Budget par le back" (Shortcut ID: 21594)
export const getInscriptionsModalColumns = (
    t: TFunction,
    budgetProposal: BudgetProposalT,
    budgetProposals: BudgetProposalT[],
    imputationRawDimensions: string[],
    projects: Project[],
): ColDef[] => {
    const columns = [
        ...getInscriptionsGridColumns(
            t,
            budgetProposals,
            getImputationAsString(imputationRawDimensions),
            getIsEditablePhase(budgetProposal.phase), // (explo-AMP Shortcut ID : 28345) Looks like this check could be done in backend ?
            projects,
            budgetProposal.exercice,
            budgetProposal.phase,
        ),
        ...getInscriptionsGridColumnsWithPhase(budgetProposal.phase, t),
    ];

    return getInscriptionsModalOrderedColumns(columns);
};
